import {
  PhotoResponseDto,
  ProfessionalUserProfileResponse,
  UserLoginResDto,
} from '@docbay/schemas';
import { parseJwt } from 'common/helpers/parseJwt';
import { StorageFields } from '../constants';
import { UserRoles } from '../constants/userRoles';
import { SupportedCountries } from 'common/types/countries';
import i18next from 'i18next';

/**
 * Enum for the keys used in localStorage
 */
export enum AuthLocalStorage {
  accessToken = 'accessToken',
  refreshToken = 'refreshToken',
  accessTokenExpiresAt = 'accessTokenExpiresAt',
  profiles = 'profiles',
  userTimezone = 'userTimezone',
  userCountryIso = 'userCountryIso',
  userLanguage = 'i18nextLng',
}

/**
 * Interface for a Profile object
 */
export interface Profile {
  token: string;
  refreshToken: string;
  email: string;
  firstName: string;
  lastName: string;
  photo: PhotoResponseDto;
  id: string;
  userTypes?: string;
  cognitoUuid?: string;
  userCountryIso?: string;
  loginAsCognitoUuid?: string;
}

/**
 * Function to set the authentication data in localStorage
 */
export const setUsersAuthSessionStorage = (value: UserLoginResDto) => {
  localStorage.setItem(
    AuthLocalStorage.accessToken,
    JSON.stringify(value.token),
  );
  localStorage.setItem(
    AuthLocalStorage.refreshToken,
    JSON.stringify(value.refreshToken),
  );

  localStorage.setItem(AuthLocalStorage.accessTokenExpiresAt, value.expiresAt);

  const token = parseJwt(value.token);

  localStorage.setItem(StorageFields.userRole, token['custom:userTypes']);
  localStorage.setItem(StorageFields.userId, value.user?.id as string);
  localStorage.setItem(
    StorageFields.userPermissions,
    token['custom:permissions'],
  );

  token['custom:timezone'] &&
    localStorage.setItem(
      AuthLocalStorage.userTimezone,
      token['custom:timezone'],
    );

  if (token && token['custom:privateDoctor']) {
    localStorage.setItem(
      StorageFields.privateDoctor,
      token['custom:privateDoctor'],
    );
  }

  if (token && token['custom:proPermissions']?.length) {
    localStorage.setItem(
      StorageFields.userProPermissions,
      JSON.stringify(token['custom:proPermissions']),
    );
  }

  if (token && token['custom:countryIso']) {
    localStorage.setItem(
      StorageFields.userCountryIso,
      token['custom:userCountryIso'] || SupportedCountries.Portugal,
    );
  }

  if (token && token['custom:languageIso']) {
    i18next.changeLanguage(token['custom:languageIso'].toLowerCase());
    localStorage.setItem(
      AuthLocalStorage.userLanguage,
      token['custom:languageIso'] || SupportedCountries.Portugal,
    );
  }
};

/**
 * Function to set the refresh token and related data in localStorage
 */
export const setUsersRefreshTokenSessionStorage = (value: {
  token: string;
  expiresAt: string;
}) => {
  localStorage.setItem(
    AuthLocalStorage.accessToken,
    JSON.stringify(value.token),
  );

  localStorage.setItem(AuthLocalStorage.accessTokenExpiresAt, value.expiresAt);

  const parsedToken = parseJwt(value.token);

  if (parsedToken && parsedToken['custom:proPermissions']?.length) {
    localStorage.setItem(
      StorageFields.userProPermissions,
      JSON.stringify(parsedToken['custom:proPermissions']),
    );
  }
  if (parsedToken && parsedToken['custom:countryIso']) {
    localStorage.setItem(
      StorageFields.userCountryIso,
      parsedToken['custom:userCountryIso'] || SupportedCountries.Portugal,
    );
  }
  if (parsedToken && parsedToken['custom:timezone']) {
    localStorage.setItem(
      AuthLocalStorage.userTimezone,
      parsedToken['custom:timezone'],
    );
  }
};

/**
 * Function to get the profiles from localStorage
 */
export const getProfilesFromSessionStorage = (): Profile[] | null => {
  const profiles = localStorage.getItem(AuthLocalStorage.profiles);

  return profiles ? (JSON.parse(profiles) as Profile[]) : null;
};

/**
 * Function to set the profiles in localStorage
 */
export const setProfilesInSessionStorage = (profiles: Profile[]) => {
  const parsedProfile = profiles.map((item) => {
    const jwtData = parseJwt(item.token);
    return {
      ...item,
      userTypes: jwtData ? jwtData['custom:userTypes'] : '',
      cognitoUuid: jwtData ? jwtData.sub : '',
      loginAsCognitoUuid: jwtData ? jwtData['custom:loginAs'] : '',
    };
  });
  localStorage.setItem(
    AuthLocalStorage.profiles,
    JSON.stringify(parsedProfile),
  );

  localStorage.setItem(StorageFields.userRole, parsedProfile[0].userTypes);
};

/**
 * Function to get the user role from localStorage
 */
export const getUserRole = () => localStorage.getItem(StorageFields.userRole);

/**
 * Function to get the user id from localStorage
 */
export const getUserId = () => localStorage.getItem(StorageFields.userId);

/**
 * Function to get the user permissions from localStorage
 */
export const getUserPermissions = () =>
  localStorage.getItem(StorageFields.userPermissions);

/**
 * Function to get the token from localStorage
 */
export const getToken = () => {
  const token = localStorage.getItem(AuthLocalStorage.accessToken);
  return token ? JSON.parse(token) : '';
};

/**
 * Function to check if the user is a private doctor
 */
export const getIsUserPrivateDoctor = () =>
  Boolean(localStorage.getItem(StorageFields.privateDoctor));

/**
 * Function to check if the user should see their agenda
 */
export const getIsShowMyAgenda = () => {
  return (
    localStorage.getItem(StorageFields.privateDoctor) &&
    localStorage
      .getItem(StorageFields.userRole)
      ?.includes(UserRoles.professional)
  );
};

/**
 * Function to get the professional permissions from localStorage
 */
export const getUserProPermissions = () => {
  const data = localStorage.getItem(StorageFields.userProPermissions);
  if (data) {
    return JSON.parse(data);
  }
};

/**
 * Function to get the current profile's Cognito UUID from localStorage
 */
export const getCurrentProfileCognitoUuid = () => {
  const profiles = getProfilesFromSessionStorage();
  return profiles ? profiles[0]?.cognitoUuid : '';
};

/**
 * Function to get the login as current profile's Cognito UUID from localStorage
 */
export const getLoginAsCurrentProfileCognitoUuid = () => {
  const profiles = getProfilesFromSessionStorage();
  return profiles ? profiles[0]?.loginAsCognitoUuid : '';
};

/**
 * Function to get the user timezone from localStorage
 */
export const getUserTimezone = () => {
  const timezone = localStorage.getItem(AuthLocalStorage.userTimezone);
  return timezone ? timezone : '';
};

/**
 * Function to set the user timezone in localStorage
 */
export const setUserTimezoneInLocalStorage = (timezone: string) => {
  localStorage.setItem(AuthLocalStorage.userTimezone, timezone);
};

/**
 * Function to get the user country ISO code from localStorage
 */
export const getUserCountryIso = () => {
  const storedCountry =
    localStorage.getItem(StorageFields.userCountryIso) || '';

  switch (storedCountry.toUpperCase()) {
    case 'PT':
      return 'PT';
    case 'FR':
      return 'FR';
    default:
      return 'PT';
  }
};

/**
 * Function to set data in localStorage
 */
export const setDataToLocalStorage = (key: StorageFields, data: any) => {
  localStorage.setItem(key, JSON.stringify(data));
};

/**
 * Function to get data from localStorage
 */
export const getFromLocalStorage = (key: StorageFields) => {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

/**
 * Function to get the user profile from localStorage
 */
export const getUserProfileFromLocalStorage = () => {
  const profile = getFromLocalStorage(StorageFields.userProfile);
  return profile as ProfessionalUserProfileResponse | null;
};
